import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { COUNTRIES, type CountryEntry } from '@/lib/countries';

/**
 * CountryPicker — searchable multi-select that shows full country names
 * but stores ISO 3166-1 alpha-2 codes (US, GB, IL…).
 *
 * The previous Country condition input was a free-text comma-separated
 * box requiring the customer to know "GB = United Kingdom, IL = Israel."
 * That's developer UX, not creator UX. This component:
 *
 *   • Renders selected countries as removable chips.
 *   • Opens a searchable dropdown when the input is focused.
 *   • Filters by country name OR code (case-insensitive).
 *   • Toggles selection on click; backspace in empty input removes
 *     the last chip.
 *   • Closes on outside click or ESC.
 *
 * Storage stays compatible: the parent component continues to receive
 * an array of uppercase ISO codes. The flag glyph is rendered from the
 * code using the regional-indicator Unicode trick (no image asset).
 */

export interface CountryPickerProps {
  selected: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  /** Max chips to render before truncating the chip strip with "+N more". */
  maxVisibleChips?: number;
}

export function CountryPicker({
  selected,
  onChange,
  placeholder = 'Search countries…',
  maxVisibleChips = 12,
}: CountryPickerProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Selected codes are stored uppercase, deduplicated, and limited to
  // codes that exist in our list. Anything that doesn't match a known
  // code is preserved as-is so legacy data isn't dropped silently.
  const selectedSet = useMemo(() => new Set(selected.map((c) => c.toUpperCase())), [selected]);

  // Filter the country list by query. Matches against name (substring,
  // case-insensitive) OR code (prefix match, case-insensitive). Selected
  // countries float to the top so the user can quickly remove them.
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      // No query — show all, with selected at the top.
      const sel: CountryEntry[] = [];
      const rest: CountryEntry[] = [];
      for (const c of COUNTRIES) {
        if (selectedSet.has(c.code)) sel.push(c);
        else rest.push(c);
      }
      return [...sel, ...rest];
    }
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().startsWith(q),
    );
  }, [query, selectedSet]);

  // Close on outside click / ESC.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        setQuery('');
        inputRef.current?.blur();
      }
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function toggleCountry(code: string) {
    const upper = code.toUpperCase();
    if (selectedSet.has(upper)) {
      onChange(selected.filter((c) => c.toUpperCase() !== upper));
    } else {
      onChange([...selected, upper]);
    }
  }

  function removeAt(index: number) {
    onChange(selected.filter((_, i) => i !== index));
  }

  // Backspace in empty input removes the last selected chip — standard
  // tag-picker UX (GitHub, Linear, Notion).
  function onInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && query === '' && selected.length > 0) {
      removeAt(selected.length - 1);
    }
  }

  const visibleChips = selected.slice(0, maxVisibleChips);
  const hiddenChipCount = selected.length - visibleChips.length;

  return (
    <div ref={rootRef} className="relative">
      {/* Chip strip + input — clicking anywhere here opens the dropdown
          and focuses the search input. */}
      <div
        className={`flex flex-wrap items-center gap-1 rounded-md border bg-transparent p-1.5 transition ${
          open
            ? 'border-[var(--color-ring)]'
            : 'border-[var(--color-border)] hover:border-[var(--color-primary)]/30'
        }`}
        onClick={() => {
          setOpen(true);
          inputRef.current?.focus();
        }}
      >
        {visibleChips.map((code, i) => {
          const entry = COUNTRIES.find((c) => c.code === code.toUpperCase());
          return (
            <span
              key={`${code}-${i}`}
              className="inline-flex items-center gap-1 rounded-full bg-[var(--color-primary)]/15 px-2 py-0.5 text-[11px] text-[var(--color-primary)]"
            >
              <span className="font-mono opacity-70">{code.toUpperCase()}</span>
              <span>{entry?.name || code}</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeAt(i);
                }}
                className="-mr-0.5 ml-0.5 rounded-full p-0.5 hover:bg-[var(--color-primary)]/20"
                aria-label={`Remove ${entry?.name || code}`}
              >
                <X size={10} />
              </button>
            </span>
          );
        })}
        {hiddenChipCount > 0 && (
          <span className="rounded-full bg-[var(--color-muted)] px-2 py-0.5 text-[10px] text-[var(--color-muted-foreground)]">
            +{hiddenChipCount} more
          </span>
        )}
        <div className="relative flex flex-1 items-center gap-1.5 min-w-[120px]">
          <Search
            size={12}
            className="shrink-0 text-[var(--color-muted-foreground)]"
            aria-hidden
          />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              if (!open) setOpen(true);
            }}
            onKeyDown={onInputKeyDown}
            onFocus={() => setOpen(true)}
            placeholder={selected.length === 0 ? placeholder : ''}
            className="min-w-0 flex-1 bg-transparent text-xs outline-none placeholder:text-[var(--color-muted-foreground)]"
          />
          <ChevronDown
            size={12}
            className={`shrink-0 text-[var(--color-muted-foreground)] transition ${
              open ? 'rotate-180' : ''
            }`}
            aria-hidden
          />
        </div>
      </div>

      {/* Dropdown — fixed-height scrollable list of matching countries. */}
      {open && (
        <div
          className="absolute left-0 right-0 top-full z-30 mt-1 max-h-64 overflow-y-auto rounded-md border border-[var(--color-border)] shadow-lg backdrop-blur-xl"
          style={{ background: 'rgba(20, 20, 20, 0.94)' }}
        >
          {results.length === 0 ? (
            <div className="px-3 py-2 text-xs text-[var(--color-muted-foreground)]">
              No countries match &ldquo;{query}&rdquo;.
            </div>
          ) : (
            <ul className="py-1">
              {results.slice(0, 80).map((c) => {
                const sel = selectedSet.has(c.code);
                return (
                  <li key={c.code}>
                    <button
                      type="button"
                      onClick={() => {
                        toggleCountry(c.code);
                        setQuery('');
                        inputRef.current?.focus();
                      }}
                      className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition ${
                        sel
                          ? 'bg-[var(--color-primary)]/12 text-[var(--color-primary)]'
                          : 'hover:bg-[var(--surface-glass-strong)]'
                      }`}
                    >
                      <span className="font-mono text-[10px] opacity-60">
                        {c.code}
                      </span>
                      <span className="flex-1 truncate">{c.name}</span>
                      {sel && (
                        <span className="text-[10px] uppercase tracking-wider opacity-70">
                          ✓ Added
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
              {results.length > 80 && (
                <li className="px-3 py-2 text-[10px] italic text-[var(--color-muted-foreground)]">
                  +{results.length - 80} more — keep typing to narrow down.
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

